import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

function highlight(code) {
  const kws = ["import","from","as","def","class","return","pass","if","else","elif",
    "for","while","in","not","and","or","True","False","None","with","raise","try",
    "except","finally","lambda","yield","super","self"];
  const builtins = ["print","range","len","sleep","append","format","enumerate",
    "isinstance","hasattr","getattr","setattr","list","dict","set","tuple","str","int","float"];
  const threadKws = ["Thread","Process","Lock","RLock","Semaphore","Event","Queue",
    "ThreadPoolExecutor","current_thread","enumerate","threading","time","random",
    "start","join","run","acquire","release","name","target","daemon","is_alive"];
  return code.split("\n").map((line, li) => {
    if (line.trimStart().startsWith("#")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i]==="#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i=line.length; continue;
      }
      if (line[i]==="'" || line[i]==='"') {
        const q=line[i]; let j=i+1;
        while(j<line.length && line[j]!==q) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i=j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j=i; while(j<line.length && /[\d_.x]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i=j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j=i; while(j<line.length && /\w/.test(line[j])) j++;
        const word=line.slice(i,j);
        let color=T.textPri;
        if (kws.includes(word)) color=T.codeKw;
        else if (threadKws.includes(word)) color=T.codeFn;
        else if (builtins.includes(word)) color=T.accent;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i=j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

function CodeBlock({ code, label="python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"5px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"12px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function Difficulty({ level }) {
  const cfg = {
    Easy:   { bg:"rgba(52,211,153,.12)", color:T.green,  border:"rgba(52,211,153,.3)"  },
    Medium: { bg:"rgba(252,211,77,.12)", color:T.yellow, border:"rgba(252,211,77,.3)"  },
    Hard:   { bg:"rgba(248,113,113,.12)",color:T.red,    border:"rgba(248,113,113,.3)" },
  }[level] || {};
  return (
    <span style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
      padding:"2px 8px",borderRadius:20,background:cfg.bg,color:cfg.color,
      border:`1px solid ${cfg.border}`,whiteSpace:"nowrap"}}>
      {level}
    </span>
  );
}

function ExCard({ num, title, difficulty, goal, requirements=[], starter, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
      marginBottom:16,overflow:"hidden"}}>
      <div style={{padding:"14px 18px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
          <span style={{fontSize:11,fontWeight:700,color:T.textMute,letterSpacing:1,textTransform:"uppercase"}}>{num}</span>
          <span style={{fontSize:14,fontWeight:700,color:T.textPri,flex:1}}>{title}</span>
          <Difficulty level={difficulty} />
        </div>
        <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,margin:"0 0 10px"}}>{goal}</p>
        {requirements.length>0 && (
          <ul style={{margin:"0 0 12px",paddingLeft:20}}>
            {requirements.map((r,i)=>(
              <li key={i} style={{fontSize:13,color:T.textSec,lineHeight:1.7,marginBottom:2}}>{r}</li>
            ))}
          </ul>
        )}
        {children}
        {starter && (
          <div>
            <div style={{fontSize:11,fontWeight:600,color:T.textMute,letterSpacing:1,
              textTransform:"uppercase",marginBottom:6}}>Starter Skeleton</div>
            <CodeBlock code={starter} />
          </div>
        )}
        <button onClick={()=>setOpen(o=>!o)}
          style={{background:"none",border:`1px solid ${T.border}`,borderRadius:7,
            padding:"5px 12px",cursor:"pointer",fontSize:12,color:T.textMute,fontFamily:"inherit",marginTop:4}}>
          {open?"▾ Hide hint":"▸ Show hint"}
        </button>
        {open && (
          <div style={{marginTop:10,background:"#060D18",border:`1px solid ${T.green}33`,
            borderRadius:8,padding:"10px 14px",fontSize:12.5,color:T.green,fontFamily:"monospace",lineHeight:1.7}}>
            {hintFor(num)}
          </div>
        )}
      </div>
    </div>
  );
}

function hintFor(num) {
  const hints = {
    "1.1": "Use threading.Thread(target=print_numbers, args=(n,)) for each thread. Call .start() on both before .join() on either.",
    "1.2": "time.sleep(size_mb * 0.1) simulates the download. Use time.time() before and after all joins to measure total elapsed time.",
    "1.3": "Create results = [None] * 5 before threads start. Each thread writes results[index] = n * n. After joining all threads, print results.",
    "2.1": "threading.current_thread().name returns the current thread's name. Default names are Thread-1, Thread-2, etc.",
    "2.2": "Thread(target=worker, name='Worker-A'). The name= keyword argument sets the thread name at creation.",
    "2.3": "for i in range(5): t = Thread(target=..., name=f'Task-{i}'). Use threading.enumerate() to list running threads (includes main thread).",
    "3.1": "class CounterThread(threading.Thread): call super().__init__(name=name) in __init__. Override run(), not start().",
    "3.2": "self.threads = []. In start_downloads, for each file create a Thread targeting self.download and append to self.threads. start() them all, then wait_all() joins all.",
    "3.3": "The race condition: thread reads balance, another thread reads balance, both add 10 and write. One write overwrites the other. Final balance < 10000.",
    "4.1": "self.lock = threading.Lock(). In deposit: with self.lock: old = self.balance; time.sleep(0.001); self.balance = old + amount",
    "4.2": "print_lock = threading.Lock() at module level. def log(msg): with print_lock: print(msg)",
    "4.3": "Deadlock: T1 holds lock_a, waits for lock_b. T2 holds lock_b, waits for lock_a. Fix: both acquire in order (lock_a then lock_b).",
    "5.1": "def increment(self): with self.lock: self.increment_twice(). def increment_twice(self): with self.lock: ... — second with hangs forever.",
    "5.2": "Change threading.Lock() to threading.RLock(). RLock tracks which thread owns it and allows the same thread to acquire it again.",
    "5.3": "def add(self, n): if n<=0: return; with self.lock: self.count+=1; self.add(n-1). RLock count goes up with each recursive acquire, unwound on return.",
    "6.1": "self.lock = threading.RLock(). def sell(self, name): with self.lock: if self.tickets > 0: self.tickets -= 1. Print before decrement to show remaining.",
    "Discussion": "Think about: thread safety (shared mutable state needs locking), when RLock is needed vs Lock (reentrant call chains), deadlock causes (circular wait), and overhead (RLock is slightly slower due to owner tracking).",
  };
  return hints[num] || "Think through what state is shared between threads, and where race conditions could occur.";
}

// ── SECTION COMPONENTS ────────────────────────────────────────────────────────

function S1() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.accent,fontFamily:"monospace"}}>threading.Thread(target, args)</code>,
      {" "}<code style={{color:T.accent,fontFamily:"monospace"}}>.start()</code>,
      {" "}<code style={{color:T.accent,fontFamily:"monospace"}}>.join()</code>.
      These three primitives are the foundation of everything else.
    </div>
    <ExCard
      num="1.1" title="Your First Threads" difficulty="Easy"
      goal='Write a function print_numbers(n) that prints numbers from 1 to n with a 0.5 second delay between each. Start two threads calling this function with different values of n, and make sure the main program waits for both before printing "All done".'
      starter={`import threading\nimport time\n\ndef print_numbers(n):\n    # TODO\n    pass\n\n# TODO: create two threads, start them, join them`}
    />
    <ExCard
      num="1.2" title="Passing Multiple Arguments" difficulty="Easy"
      goal='Write a function download_file(filename, size_mb) that simulates a download by sleeping size_mb * 0.1 seconds, then prints "{filename} downloaded". Launch 3 threads downloading different files of different sizes, all running concurrently.'
      requirements={[
        "All 3 downloads must run concurrently (not one after another)",
        "Measure and print total elapsed time — confirm it's close to the slowest download, not the sum of all three",
        "Use args=(...) in Thread() to pass both arguments",
      ]}
    />
    <ExCard
      num="1.3" title="Thread Return Values" difficulty="Medium"
      goal="Threads don't return values directly. Write a function square(n, results, index) that stores n * n into a shared list results at position index."
      requirements={[
        "Create results = [None] * 5 before launching threads",
        "Launch 5 threads squaring numbers 1–5",
        "After all threads finish, print the results list in order",
        "No global variables — pass results and index via args=",
      ]}
    />
  </div>
);}

function S2() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.accent,fontFamily:"monospace"}}>threading.current_thread().name</code>,
      the <code style={{color:T.accent,fontFamily:"monospace"}}>name=</code> argument,
      and <code style={{color:T.accent,fontFamily:"monospace"}}>threading.enumerate()</code>.
      Good names make debugging multi-threaded programs vastly easier.
    </div>
    <ExCard
      num="2.1" title="Print the Current Thread's Name" difficulty="Easy"
      goal='Write a function worker() that prints f"{threading.current_thread().name} is running". Start 3 threads without setting custom names and observe the default names Python assigns.'
      requirements={[
        "No name= argument when creating threads",
        "Observe what Python assigns: Thread-1, Thread-2, Thread-3",
        "Also call worker() directly from main — what name do you see?",
      ]}
    />
    <ExCard
      num="2.2" title="Custom Thread Names" difficulty="Easy"
      goal='Repeat 2.1 but give each thread a meaningful name ("Worker-A", "Worker-B", "Worker-C") using the name= argument. Confirm the custom names show up in the printed output.'
      requirements={[
        'Thread(target=worker, name="Worker-A")',
        "Verify the output shows your custom names, not Thread-1 etc.",
      ]}
    />
    <ExCard
      num="2.3" title="Naming Threads Dynamically in a Loop" difficulty="Medium"
      goal='Create 5 threads in a for loop, naming each one f"Task-{i}". Each thread prints its own name and sleeps for a random time between 0.1 and 1 second.'
      requirements={[
        "Use a for loop to create all 5 threads",
        "After starting all threads, loop over threading.enumerate() and print active thread names",
        "After joining all threads, print thread names again — only MainThread should remain",
        "Use random.uniform(0.1, 1.0) for sleep duration",
      ]}
    />
  </div>
);}

function S3() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: subclassing <code style={{color:T.accent,fontFamily:"monospace"}}>threading.Thread</code>,
      overriding <code style={{color:T.codeFn,fontFamily:"monospace"}}>run()</code>.
      Classes give you a natural place to store thread-specific state without global variables.
    </div>
    <ExCard
      num="3.1" title="Subclass threading.Thread" difficulty="Medium"
      goal="Instead of passing a function to Thread(target=...), create a class CounterThread that overrides run(). The class accepts a limit in its constructor and counts from 1 to limit, printing its own thread name alongside each number."
      requirements={[
        "Call super().__init__(name=name) in __init__",
        "Override run(), not start()",
        "Create 3 instances with different limits and names",
        "Start and join all three",
      ]}
      starter={`import threading\nimport time\n\nclass CounterThread(threading.Thread):\n    def __init__(self, limit, name=None):\n        super().__init__(name=name)\n        # TODO: store limit\n\n    def run(self):\n        # TODO: count from 1 to self.limit\n        # print thread name + number each iteration\n        pass`}
    />
    <ExCard
      num="3.2" title="A Class Managing Multiple Threads" difficulty="Medium"
      goal="Write a class DownloadManager with start_downloads(file_list) that creates one thread per file, stores them in self.threads, starts them all, and has a wait_all() method that joins them."
      requirements={[
        "self.threads = [] in __init__",
        "A download(filename) method that simulates the download (sleep + print)",
        "start_downloads() creates threads targeting self.download, appends to self.threads, starts them",
        "wait_all() joins every thread in self.threads",
        "Demonstrate with 4 files",
      ]}
    />
    <ExCard
      num="3.3" title="Shared State Inside a Class — Race Condition" difficulty="Hard"
      goal="Write a class BankAccount with a balance attribute and a deposit(amount) method that has a tiny time.sleep(0.001) between reading and writing balance to make race conditions visible."
      requirements={[
        "10 threads each call deposit(10) 100 times on the same BankAccount",
        "Expected final balance: 10 × 100 × 10 = 10,000",
        "Observe the actual balance — it will likely be less",
        "Explain in a comment why the race condition occurs (this sets up Section 4)",
      ]}
    >
      <div style={{background:"rgba(248,113,113,.08)",border:`1px solid ${T.red}33`,
        borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12.5,color:T.red,lineHeight:1.65}}>
        <strong>Expected outcome:</strong> Final balance &lt; 10,000 most runs. Race condition: Thread A reads balance=500, Thread B reads balance=500, both write 510 — one increment is lost.
      </div>
    </ExCard>
  </div>
);}

function S4() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.codeFn,fontFamily:"monospace"}}>threading.Lock()</code>,
      {" "}<code style={{color:T.codeKw,fontFamily:"monospace"}}>with self.lock:</code> context manager.
      A Lock ensures only one thread executes the protected block at a time.
    </div>
    <ExCard
      num="4.1" title="Fix the Race Condition" difficulty="Medium"
      goal="Take BankAccount from Exercise 3.3 and add a threading.Lock(). Wrap the read-modify-write inside deposit() with 'with self.lock:'. Re-run the 10×100 test and confirm the final balance is always 10,000."
      requirements={[
        "self.lock = threading.Lock() in __init__",
        "with self.lock: wraps the entire read → sleep → write sequence",
        "Run 5 times and confirm the result is always exactly 10,000",
      ]}
      starter={`import threading\nimport time\n\nclass BankAccount:\n    def __init__(self):\n        self.balance = 0\n        self.lock = threading.Lock()\n\n    def deposit(self, amount):\n        with self.lock:\n            # TODO: read, sleep(0.001), write\n            pass`}
    />
    <ExCard
      num="4.2" title="Locking Around a Print Statement" difficulty="Easy"
      goal="Without a lock, output from multiple threads printing simultaneously can interleave. Write a log(message) function that acquires a shared Lock before print() and releases it after."
      requirements={[
        "print_lock = threading.Lock() at module level",
        "def log(message): with print_lock: print(message)",
        "Launch 5 threads each calling log(...) 20 times",
        "Verify no two thread names appear on the same line",
      ]}
    />
    <ExCard
      num="4.3" title="Deadlock Demonstration" difficulty="Hard"
      goal="Create two Lock objects, lock_a and lock_b. Write task_1() that acquires lock_a then lock_b, and task_2() that acquires lock_b then lock_a. Run both in threads and observe the hang. Then fix it."
      requirements={[
        "Part A: demonstrate the deadlock (program will hang — use Ctrl+C to stop)",
        "Part B: fix by making both functions acquire locks in the same order (always lock_a then lock_b)",
        "Add a small time.sleep(0.1) after the first acquire in each function to make the deadlock more reliable",
        "Add a print inside each acquire to confirm Part B no longer hangs",
      ]}
    >
      <div style={{background:"rgba(252,211,77,.08)",border:`1px solid ${T.yellow}33`,
        borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12.5,color:T.yellow,lineHeight:1.65}}>
        <strong>Why it deadlocks:</strong> T1 holds lock_a and waits for lock_b. T2 holds lock_b and waits for lock_a. Neither can proceed — circular wait.
      </div>
    </ExCard>
  </div>
);}

function S5() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.codeFn,fontFamily:"monospace"}}>threading.RLock()</code> — a reentrant lock.
      The same thread can acquire it multiple times without deadlocking.
      Each acquire must be matched with a release.
    </div>
    <ExCard
      num="5.1" title="Why a Regular Lock Fails Here" difficulty="Medium"
      goal="Write a class Node with increment() that acquires a Lock, then calls increment_twice() which also tries to acquire the same lock. Run with a single thread and observe the hang."
      requirements={[
        "self.lock = threading.Lock()",
        "def increment(self): with self.lock: self.increment_twice()",
        "def increment_twice(self): with self.lock: self.value += 2",
        "Run from main thread — program hangs because the same Lock cannot be acquired twice by the same thread",
      ]}
    />
    <ExCard
      num="5.2" title="Fix It with RLock" difficulty="Medium"
      goal="Replace the Lock in Exercise 5.1 with threading.RLock(). Confirm the program no longer hangs — RLock tracks which thread owns it and allows re-acquisition."
      requirements={[
        "Change only one line: Lock() → RLock()",
        "Everything else stays identical",
        "Verify the program completes and prints the correct value",
        "Add a comment explaining what 'reentrant' means",
      ]}
    />
    <ExCard
      num="5.3" title="Recursive Function Protected by RLock" difficulty="Hard"
      goal="Write a class SharedCounter with an RLock and a recursive method add(n) that acquires the lock, increments an internal counter by 1, and calls self.add(n-1) while still holding the lock."
      requirements={[
        "if n <= 0: return (base case)",
        "with self.lock: self.count += 1; self.add(n-1)",
        "Launch 3 threads each calling add(200) on the same SharedCounter",
        "Confirm final counter is exactly 600",
        "Keep n ≤ 500 (Python default recursion limit is ~1000; each call = one frame)",
        "Add a comment: why would plain Lock deadlock immediately on the second recursive call?",
      ]}
    />
  </div>
);}

function S6() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Combines: custom thread classes, <code style={{color:T.codeFn,fontFamily:"monospace"}}>RLock</code>,
      thread names, shared mutable state, concurrent access. Build something realistic.
    </div>
    <ExCard
      num="6.1" title="Put It All Together — TicketBooth" difficulty="Hard"
      goal="Design a class TicketBooth that manages 50 tickets. Its sell(customer_name) method checks availability, decrements if available, and prints the result. 20 customer threads compete concurrently."
      requirements={[
        "self.lock = threading.RLock() (RLock in case you add helper methods that also need the lock)",
        'sell() prints the current thread name + customer name, checks tickets, either sells or prints "sold out"',
        'Give each thread a custom name: "Customer-1" … "Customer-20"',
        "Confirm exactly 50 tickets are sold — no more, no less",
        "Confirm no two threads ever report the same remaining count (e.g. '49 left' should appear exactly once)",
        "Bonus: track which customer got each ticket and print the full list at the end",
      ]}
      starter={`import threading\n\nclass TicketBooth:\n    def __init__(self, total):\n        self.tickets = total\n        self.lock = threading.RLock()\n\n    def sell(self, customer_name):\n        with self.lock:\n            # TODO\n            pass\n\nbooth = TicketBooth(50)\nthreads = [\n    threading.Thread(\n        target=booth.sell,\n        args=(f"Customer-{i}",),\n        name=f"Customer-{i}"\n    )\n    for i in range(1, 21)\n]\nfor t in threads: t.start()\nfor t in threads: t.join()\nprint(f"Tickets remaining: {booth.tickets}")`}
    />
  </div>
);}

function SDiscussion() {
  const [open, setOpen] = useState({});
  const qs = [
    {
      q:"What's the difference between a thread's default name and one you set explicitly with name=? Where might default names become confusing?",
      a:"Default names (Thread-1, Thread-2, ...) are sequential integers assigned at creation time. They become ambiguous in large programs where the same number can reappear after threads finish and new ones start. Custom names like 'DB-Writer' or 'Health-Check' make logs readable and distinguish threads by role rather than creation order.",
    },
    {
      q:"When subclassing threading.Thread, why do we override run() instead of start()?",
      a:"start() is the public API — it sets up the thread's internals, changes its state to 'alive', and then calls run() in the new OS thread. If you override start(), you break all that setup. run() is the hook designed for subclasses: it's what executes in the new thread after everything is ready. The rule: override run(), call start().",
    },
    {
      q:"Why is a plain Lock unsafe for a function that might call itself (directly or indirectly) while already holding the lock?",
      a:"A plain Lock has no concept of 'owner'. When a thread that already holds the lock tries to acquire it again, the lock doesn't recognise it's the same thread — it just blocks, waiting for a release that will never come (because the release is after the nested acquire, which never completes). RLock solves this by tracking which thread owns it and a count of acquisitions.",
    },
    {
      q:"What's the performance cost of using RLock everywhere instead of Lock where a plain Lock would suffice?",
      a:"RLock stores extra state: the owning thread's identity and an acquisition count. Each acquire/release must check and update these, adding a small but non-zero overhead. In a tight loop doing millions of lock operations this can be measurable. Rule of thumb: use Lock as the default; switch to RLock only when you have reentrant code paths that need it.",
    },
    {
      q:"In Exercise 4.3, why does swapping the acquisition order fix the deadlock?",
      a:"Deadlocks require a circular wait: T1 holds A and wants B, T2 holds B and wants A. If both threads always acquire locks in the same global order (always A before B), the circular dependency is impossible: whichever thread gets A first will also get B before the other thread can try for A. This is called 'lock ordering' and is the simplest deadlock prevention strategy.",
    },
  ];
  return (
    <div>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
        padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
        No code required — think through these before reading the answers.
        These are the questions interviewers actually ask.
      </div>
      {qs.map((item,i)=>(
        <div key={i} style={{background:T.card,border:`1px solid ${T.border}`,
          borderRadius:12,marginBottom:12,overflow:"hidden"}}>
          <button onClick={()=>setOpen(o=>({...o,[i]:!o[i]}))}
            style={{width:"100%",background:"none",border:"none",cursor:"pointer",
              padding:"14px 18px",textAlign:"left",fontFamily:"inherit",
              display:"flex",alignItems:"flex-start",gap:12}}>
            <span style={{color:T.accent,fontWeight:700,fontSize:13,minWidth:28,marginTop:1}}>Q{i+1}.</span>
            <span style={{fontSize:13.5,color:T.textPri,fontWeight:500,lineHeight:1.6,flex:1}}>{item.q}</span>
            <span style={{color:T.textMute,fontSize:16,marginTop:1}}>{open[i]?"▾":"▸"}</span>
          </button>
          {open[i] && (
            <div style={{padding:"0 18px 16px 58px",fontSize:13,color:T.textSec,lineHeight:1.75}}>
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── SECTIONS METADATA ────────────────────────────────────────────────────────
const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",               sub:"All 6 sections at a glance"},
  {id:"s1",   icon:"🧵", title:"Section 1 — Basic Threads",       sub:"target, args, start, join"},
  {id:"s2",   icon:"🏷",  title:"Section 2 — Thread Names",        sub:"current_thread, name=, enumerate"},
  {id:"s3",   icon:"🏗",  title:"Section 3 — Thread Classes",      sub:"Subclass Thread, override run()"},
  {id:"s4",   icon:"🔒", title:"Section 4 — Lock",                sub:"Race conditions, deadlock"},
  {id:"s5",   icon:"🔁", title:"Section 5 — RLock",               sub:"Reentrant locking, recursive functions"},
  {id:"s6",   icon:"🎯", title:"Section 6 — Mixed Challenge",     sub:"TicketBooth: everything combined"},
  {id:"disc", icon:"💬", title:"Discussion Questions",            sub:"5 interview-style questions with answers"},
];

const sectionComponents = {s1:S1,s2:S2,s3:S3,s4:S4,s5:S5,s6:S6,disc:SDiscussion};

const EXERCISE_COUNTS = {s1:3,s2:3,s3:3,s4:3,s5:3,s6:1};

function HomePage({ onNavigate }) {
  const difficulties = {
    s1:["Easy","Easy","Medium"],
    s2:["Easy","Easy","Medium"],
    s3:["Medium","Medium","Hard"],
    s4:["Medium","Easy","Hard"],
    s5:["Medium","Medium","Hard"],
    s6:["Hard"],
  };
  return (
    <div>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>
          PYTHON · THREADING
        </div>
        <h1 style={{fontSize:24,fontWeight:700,color:T.textPri,margin:"0 0 10px",lineHeight:1.35}}>
          Threading — Practice Exercises
        </h1>
        <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,maxWidth:580,margin:0}}>
          16 exercises covering: creating threads, thread names, thread classes,{" "}
          <code style={{color:T.codeFn,fontFamily:"monospace"}}>Lock</code>, and{" "}
          <code style={{color:T.codeFn,fontFamily:"monospace"}}>RLock</code>.
          Each lists the goal, requirements, a starter skeleton, and a hint.
        </p>
      </div>
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {[["Easy",T.green],["Medium",T.yellow],["Hard",T.red]].map(([l,c])=>(
          <span key={l} style={{fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
            padding:"3px 10px",borderRadius:20,background:`rgba(0,0,0,.3)`,color:c,
            border:`1px solid ${c}44`}}>{l}</span>
        ))}
        <span style={{fontSize:12,color:T.textMute,alignSelf:"center"}}>16 exercises · 5 discussion questions</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {SECTIONS.filter(s=>s.id!=="home").map((s,i)=>{
          const diffs = difficulties[s.id] || [];
          return (
            <button key={s.id} onClick={()=>onNavigate(s.id)}
              style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
                padding:"12px 14px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",
                display:"flex",gap:10,alignItems:"flex-start"}}>
              <span style={{fontSize:18,lineHeight:1.2,minWidth:24}}>{s.icon}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:T.textPri,marginBottom:3}}>{s.title}</div>
                <div style={{fontSize:11,color:T.textSec,marginBottom:diffs.length?6:0}}>{s.sub}</div>
                {diffs.length>0 && (
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {diffs.map((d,j)=><Difficulty key={j} level={d}/>)}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PythonThreadingExercises() {
  const [current, setCurrent] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  const navigate = (id) => {
    setCurrent(id);
    setVisited(prev => new Set([...prev, id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const currentIdx = SECTIONS.findIndex(s => s.id === current);
  const prevSection = currentIdx > 0 ? SECTIONS[currentIdx - 1] : null;
  const nextSection = currentIdx < SECTIONS.length - 1 ? SECTIONS[currentIdx + 1] : null;
  const CurrentComponent = sectionComponents[current];
  const progressPct = Math.round((visited.size / SECTIONS.length) * 100);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 70px)",
      background:T.bg,color:T.textPri,fontFamily:"system-ui,-apple-system,sans-serif",overflow:"hidden"}}>

      {/* TOP BAR */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
        background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <button onClick={()=>setSidebarOpen(o=>!o)}
          style={{background:"none",border:"none",color:T.textSec,cursor:"pointer",fontSize:18,padding:"2px 4px",lineHeight:1}}>☰</button>
        <span style={{fontSize:15,fontWeight:600,color:T.textPri}}>🧵 Python Threading — Exercises</span>
        <div style={{flex:1,height:4,background:T.card,borderRadius:2,overflow:"hidden"}}>
          <div style={{width:`${progressPct}%`,height:"100%",background:T.accent,
            borderRadius:2,transition:"width .4s ease"}} />
        </div>
        <span style={{fontSize:12,color:T.accent,minWidth:36,textAlign:"right",fontWeight:600}}>{progressPct}%</span>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {/* SIDEBAR */}
        {sidebarOpen && (
          <div style={{width:220,minWidth:220,background:T.surface,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"8px 6px"}}>
              {SECTIONS.map(s=>(
                <button key={s.id} onClick={()=>navigate(s.id)}
                  style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",border:"none",borderRadius:7,cursor:"pointer",
                    background:current===s.id?"rgba(56,189,248,0.08)":"none",fontFamily:"inherit",marginBottom:2,
                    borderLeft:current===s.id?`2px solid ${T.accent}`:"2px solid transparent"}}>
                  <span style={{fontSize:13,minWidth:20}}>{s.icon}</span>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:11.5,
                      color:current===s.id?T.accent:visited.has(s.id)?T.green:T.textSec,
                      fontWeight:current===s.id?600:400,lineHeight:1.35}}>
                      {s.title}
                    </div>
                    <div style={{fontSize:10,color:T.textMute,marginTop:1,lineHeight:1.3}}>{s.sub}</div>
                  </div>
                  {visited.has(s.id) && current!==s.id &&
                    <span style={{fontSize:10,color:T.green}}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div ref={contentRef} style={{flex:1,overflowY:"auto"}}>
          <div style={{maxWidth:880,margin:"0 auto",padding:"24px 28px"}}>
            {current==="home" ? (
              <HomePage onNavigate={navigate} />
            ) : (
              <div>
                <div style={{marginBottom:22}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,
                    textTransform:"uppercase",marginBottom:8}}>
                    {current==="disc" ? "Discussion" : `Section ${currentIdx} of ${SECTIONS.length-2}`}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <span style={{fontSize:22}}>{SECTIONS[currentIdx].icon}</span>
                    <h1 style={{fontSize:21,fontWeight:700,color:T.textPri,margin:0}}>{SECTIONS[currentIdx].title}</h1>
                  </div>
                  <p style={{fontSize:13,color:T.textSec,margin:0}}>{SECTIONS[currentIdx].sub}</p>
                </div>
                {CurrentComponent && <CurrentComponent />}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:28,
                  paddingTop:18,borderTop:`1px solid ${T.border}`}}>
                  {prevSection ? (
                    <button onClick={()=>navigate(prevSection.id)}
                      style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:9,
                        padding:"9px 16px",cursor:"pointer",color:T.textSec,fontFamily:"inherit",fontSize:13}}>
                      ← {prevSection.icon} {prevSection.title}
                    </button>
                  ) : <div/>}
                  {nextSection && (
                    <button onClick={()=>navigate(nextSection.id)}
                      style={{background:"rgba(56,189,248,0.08)",border:`1px solid ${T.accent}44`,
                        borderRadius:9,padding:"9px 16px",cursor:"pointer",color:T.accent,
                        fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                      {nextSection.icon} {nextSection.title} →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
